import React, { useEffect, useState } from 'react';
import { managedFarmFormSteps } from '../../../../constants/managedFarm';
import { ManagedFarmFormWrapper } from '../ManagedFarmFormWrapper';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../../hooks/store/useStore';
import { managedFarmSlice } from '../../../../store/managedFarmSlice';
import { managedFarmPageInfo } from '../../../../constants/pageInfo/pageInfo.constant';
import { PopupConfirmModal } from '../../../../components/common/modal/popup/PopupConfirmModal';
import styled from 'styled-components';
import { SurveyInputHeader } from '../../../../components/common/survey/inputs/SurveyInputHeader';
import { Icon } from '../../../../components/common/icon/Icon';
import { CustomDatePicker } from '../../../../components/common/inputs/CustomDatePicker';
import { format } from 'date-fns';
import { useNotification } from '../../../../provider/ToastNotificationProvider';
import { Helmet } from 'react-helmet';
import { ampli } from '../../../../ampli';

const ERROR_MESSAGES = {
  startDate: {
    title: '예상 시작일을 선택해주세요',
    buttonName: '예상 시작일 선택하기',
  },
  endDate: {
    title: '예상 완료일을 선택해주세요',
    buttonName: '예상 완료일 선택하기',
  },
};

export const ManagedFarmFormTaskSchedulePage = () => {
  const navigate = useNavigate();
  const { openNotification } = useNotification();

  const dispatch = useAppDispatch();
  const managedFarmSelector = useAppSelector((state) => state.managedFarm);
  const managedFarmActions = managedFarmSlice.actions;

  const [startDate, setStartDate] = useState<Date | undefined>(managedFarmSelector.startDate);
  const [endDate, setEndDate] = useState<Date | undefined>(managedFarmSelector.endDate);

  const [isStartDatePickerVisible, setIsStartDatePickerVisible] = useState<boolean>(false);
  const [isEndDatePickerVisible, setIsEndDatePickerVisible] = useState<boolean>(false);

  const [inputError, setInputError] = useState<{ title: string; buttonName: string; errorHandler?: () => void }>();
  const [isShowErrorModal, setIsShowErrorModal] = useState<boolean>(false);

  const currentStep = managedFarmFormSteps.STEP3;
  const steps = Object.values(managedFarmFormSteps);

  const today = new Date();

  const handleSelectDateButtonClick = (type: 'startDate' | 'endDate') => {
    if (type === 'startDate') {
      setIsStartDatePickerVisible(true);
    }

    if (type === 'endDate') {
      if (!startDate) {
        openNotification({ message: '예상 시작일을 먼저 선택해주세요' });
        return;
      }
      setIsEndDatePickerVisible(true);
    }
  };

  const checkHasInputError = () => {
    if (!startDate) {
      setInputError({ ...ERROR_MESSAGES.startDate, errorHandler: () => setIsStartDatePickerVisible(true) });
      setIsShowErrorModal(true);
      return true;
    }

    if (!endDate) {
      setInputError({ ...ERROR_MESSAGES.endDate, errorHandler: () => setIsEndDatePickerVisible(true) });
      setIsShowErrorModal(true);

      return true;
    }

    return false;
  };

  const dispatchTaskSchedule = () => {
    if (!startDate || !endDate) {
      return;
    }

    if (startDate instanceof Date) {
      dispatch(managedFarmActions.changeStartDate(startDate));
    }

    if (endDate instanceof Date) {
      dispatch(managedFarmActions.changeEndDate(endDate));
    }
  };

  return (
    <>
      <Helmet>
        <title>{managedFarmPageInfo.formTaskSchedulePage.title}</title>
      </Helmet>
      <ManagedFarmFormWrapper
        headerTitle={'예상 작업일을 선택하세요'}
        canConfirm
        steps={steps}
        currentStep={currentStep}
        confirmHandler={() => {
          const isInputErrorExist = checkHasInputError();
          if (isInputErrorExist) {
            return;
          }
          dispatchTaskSchedule();
          if (startDate && endDate) {
            ampli.pvmGoNextPageManagedFarmTaskSchedule({
              start_date: format(startDate, 'yyyy.MM.dd'),
              end_date: format(endDate, 'yyyy.MM.dd'),
            });
          }
          navigate(managedFarmPageInfo.formTaskCheckpointPage.path, { replace: true });
        }}
        prevButtonHandler={() => {
          dispatchTaskSchedule();
          navigate(managedFarmPageInfo.formTaskTypePage.path, { replace: true });
        }}
      >
        <SurveyInputSection>
          <InputContainer>
            <SurveyInputHeader inputTitle={'예상 시작일'} isRequired />
            <SelectDateButton onClick={() => handleSelectDateButtonClick('startDate')}>
              <ButtonTextWrapper>
                <ButtonText>{startDate ? format(startDate, 'yy년 M월 d일') : '예상 시작일을 선택해주세요.'}</ButtonText>
              </ButtonTextWrapper>
              <Icon name={'AngleRight'} width={16} height={16} fill={'#898B94'} />
            </SelectDateButton>
          </InputContainer>
          <InputContainer>
            <SurveyInputHeader inputTitle={'예상 완료일'} isRequired />
            <SelectDateButton onClick={() => handleSelectDateButtonClick('endDate')}>
              <ButtonTextWrapper>
                <ButtonText>{endDate ? format(endDate, 'yy년 M월 d일') : '예상 완료일을 선택해주세요.'}</ButtonText>
              </ButtonTextWrapper>
              <Icon name={'AngleRight'} width={16} height={16} fill={'#898B94'} />
            </SelectDateButton>
          </InputContainer>
        </SurveyInputSection>
      </ManagedFarmFormWrapper>

      <PopupConfirmModal
        isShow={isShowErrorModal}
        hasCloseButton={false}
        closeModal={() => setIsShowErrorModal(false)}
        title={inputError?.title ?? ''}
        buttonArray={[
          {
            text: inputError?.buttonName ?? '',
            onClick: () => {
              setIsShowErrorModal(false);
              inputError?.errorHandler?.();
            },
            color: 'blue',
          },
        ]}
      />

      <CustomDatePicker
        title={'예상 시작일'}
        visible={isStartDatePickerVisible}
        min={today}
        defaultValue={startDate}
        onConfirm={(selectedDate) => {
          setStartDate(selectedDate);
          setEndDate(undefined);
          setIsEndDatePickerVisible(true);
        }}
        onClose={() => {
          setIsStartDatePickerVisible(false);
        }}
      />
      <CustomDatePicker
        title={'예상 완료일'}
        visible={isEndDatePickerVisible}
        onConfirm={(selectedDate) => {
          setEndDate(selectedDate);
        }}
        min={startDate ?? today}
        defaultValue={endDate}
        onClose={() => setIsEndDatePickerVisible(false)}
      />
    </>
  );
};

const SurveyInputSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  flex: 1 0 0;
  align-self: stretch;

  padding: 0px 16px;
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`;

const SelectDateButton = styled.button`
  height: 72px;
  max-height: 72px;

  display: flex;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;

  padding: 0px 16px;

  border-radius: var(--radius-medium, 8px);
  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);
`;

const ButtonTextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  flex: 1 0 0;
`;

const ButtonText = styled.span`
  color: var(--Light-Scheme-Monochrome-offblack, #505363);

  /* System/Medium/m-regular */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 85%; /* 11.9px */
`;
